import React, { useState, Fragment, useEffect } from 'react';
import styled from 'styled-components';
import { SubscriptionTiers } from 'state/subscription/SubscriptionTypes';
import Checkbox from './Checkbox';
import { RFC } from 'state/StateTypes';
import { ChevronDown, ChevronUp } from 'styled-icons/boxicons-regular';
import useOutsideClick from 'hooks/useOutsideClick';
import { useDispatch } from 'react-redux';
import { filterAssets } from 'state/assets/AssetActions';
import { StateItems } from 'state/assets/AssetTypes';
import { InputChange } from 'types';
import { theme } from 'styles/ThemeProvider';

interface Props {
   content: 'Tier' | 'Active';
   title: StateItems;
}

const TierActiveFilter: RFC<Props> = ({ content, title }) => {
   const [expanded, setExpanded] = useState<boolean>(false);
   const [tier, setTier] = useState<SubscriptionTiers | null>(null);
   const [active, setActive] = useState<boolean | null>(null);
   const dropDownRef = useOutsideClick(() => setExpanded(false));
   const dispatch = useDispatch();

   useEffect(() => {
      dispatch(filterAssets({ active, tier, target: title }));
   }, [active, tier]);

   //this logic seems ridiculous but it's a result of the checkboxes having a default state of null -- can probably be condensed/simplified later
   //my understanding is that these filters are also subject to change
   const handleFreeTierSelect = (e: InputChange) => {
      if (!e.target.checked) {
         setTier(null);
      } else {
         setTier(SubscriptionTiers.Free);
      }
   };

   const handlePaidTierSelect = (e: InputChange) => {
      if (!e.target.checked) {
         setTier(null);
      } else {
         setTier(SubscriptionTiers.Paid);
      }
   };

   const handleInactiveSelect = (e: InputChange) => {
      if (!e.target.checked) {
         setActive(null);
      } else {
         setActive(false);
      }
   };

   const handleActiveSelect = (e: InputChange) => {
      if (!e.target.checked) {
         setActive(null);
      } else {
         setActive(!active);
      }
   };

   return (
      <Container>
         <Button onClick={() => setExpanded(!expanded)}>
            {content}
            {expanded ? (
               <ChevronUp size={30} title="Toggle List Down" fontWeight={300} color={theme.primaryWhite} />
            ) : (
               <ChevronDown size={30} title="Toggle List Up" fontWeight={300} color={theme.primaryWhite} />
            )}
         </Button>
         <Content visible={expanded} ref={dropDownRef}>
            {content === 'Tier' ? (
               <Fragment>
                  <Checkbox checked={tier === SubscriptionTiers.Free} onChange={handleFreeTierSelect} label={SubscriptionTiers.Free} />
                  <Checkbox checked={tier === SubscriptionTiers.Paid} onChange={handlePaidTierSelect} label={SubscriptionTiers.Paid} />
               </Fragment>
            ) : (
               <Fragment>
                  <Checkbox checked={active === true} onChange={handleActiveSelect} label="Active" />
                  <Checkbox checked={active === false} onChange={handleInactiveSelect} label="Inactive" />
               </Fragment>
            )}
         </Content>
      </Container>
   );
};

export default TierActiveFilter;

const Container = styled.div`
   position: relative;
   display: inline-block;
`;

const Button = styled.button.attrs({ type: 'button' })`
   background-color: ${({ theme }) => theme.secondaryBlueLight};
   color: white;
   padding: 5px 7px;
   font-size: 20px;
   font-weight: 700;
   display: flex;
   justify-content: center;
   flex-direction: row;
   border: none;
   align-items: center;
   width: 90px;
   cursor: pointer;

   &:hover,
   &:focus {
      background-color: ${({ theme }) => theme.secondaryBlueDark};
   }
`;

const Content = styled.div`
   display: ${({ visible }: { visible: boolean }) => (visible ? 'block' : 'none')};
   position: absolute;
   background-color: #f1f1f1;
   min-width: 130px;
   box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
   z-index: 1;
   > * {
      padding: 6px 8px;
      align-self: center;
      &:hover {
         background-color: #ddd;
         cursor: pointer;
      }
   }
`;
